import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";

export const EmployeeTransactionViewModal = ({ isOpen, onClose, selectedData }) => {
  if (!isOpen || !selectedData) return null;

  const Field = ({ label, value, className = "" }) => (
    <div className={`flex flex-col gap-0.5 ${className}`}>
      <span className="text-[9px] uppercase tracking-[0.15em] text-slate-400 font-semibold">
        {label}
      </span>
      <span className="text-sm text-slate-700 font-medium pb-1 border-b border-slate-200 min-h-[22px]">
        {value || <span className="text-slate-300">—</span>}
      </span>
    </div>
  );

  return (

    <>

      {/* Print Styles */}
      <style>
        {`@media print {
            body * { visibility: hidden; }
            .print-area, .print-area * { visibility: visible; }
            .print-area { position: fixed; top: 0; left: 0; width: 100%; }
            .no-print { display: none !important; }
          }`}
      </style>

      <div className="fixed inset-0 z-[10000] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-auto">
        <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl flex flex-col max-h-[95vh] border border-slate-200">

          {/* Modal Chrome Header */}
          <div className="no-print flex justify-between items-center px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 bg-blue-900 rounded-full" />
              <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                Assignment Note
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => window.print()}
                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md bg-transparent border-none cursor-pointer transition-colors"
                title="Print"
              >
                <PrintIcon style={{ fontSize: 16 }} />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md bg-transparent border-none cursor-pointer transition-colors"
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </button>
            </div>
          </div>

          {/* Document */}
          <div className="print-area overflow-y-auto flex-grow">
            <div className="px-10 py-8">

              {/* Document Header */}
              <div className="text-center mb-6 pb-5 border-b-2 border-blue-900">
                <h1 className="text-2xl font-bold text-blue-900 tracking-tight uppercase">
                  Assignment Note
                </h1>
              </div>

              {/* Top Row: Destination, F/B, Vessel */}
              <div className="grid grid-cols-3 gap-4 mb-5">
                <Field label="Destination" value={selectedData.employeeInformationDestinationCode} />
                <Field label="F/B Reference" value={selectedData.fbReference} />
                <Field label="Vessel" value={selectedData.tempVesselName || selectedData.employeeInformationVesselName} />
              </div>

              {/* Formal Statement */}
              <div className="relative text-center my-5">
                {/* <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-900 rounded-full" /> */}
                <p className="pl-4 text-xs text-slate-500 italic leading-relaxed">
                  Job assignment for the above stated employee is hereby granted, to wit
                </p>
              </div>

              {/* Employee Details */}
              <div className="bg-slate-50 rounded-lg border border-slate-200 p-5 mb-5">
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <Field label="Employee Number" value={selectedData.employeeNumber} />
                  <Field label="Unit / Department" value={selectedData.departmentName} />
                  <Field label="Employee Name" value={selectedData.employeeName} />
                  <Field label="Assigned To" value={selectedData.assignedToName} />
                  <Field label="Employee Title" value={selectedData.tempPositionName || selectedData.employeePosition} />
                  <Field label="Vessel" value={selectedData.tempVesselName} />
                  <Field label="Transaction Code" value={selectedData.transactionCode} />
                  <Field
                    label="Effective From"
                    value={selectedData.effectiveFrom
                      ? new Date(selectedData.effectiveFrom).toLocaleDateString('en-US', {
                        year: 'numeric', month: 'long', day: 'numeric'
                      })
                      : null}
                  />
                </div>
              </div>

              {/* Remarks */}
              <div className="mb-5">
                <span className="text-[9px] uppercase tracking-[0.15em] text-slate-400 font-semibold block mb-1.5">
                  Remarks
                </span>
                <div className="text-sm text-slate-700 border border-slate-200 rounded-lg px-4 py-3 min-h-[56px] bg-slate-50 leading-relaxed">
                  {selectedData.remarks || <span className="text-slate-300 italic">No remarks provided</span>}
                </div>
              </div>

              {/* Closing Statement */}
              <p className="text-[11px] text-slate-400 text-center italic leading-relaxed border-t border-slate-100 pt-4 mb-6">
                In view thereof, he/she shall be authorized to work/time/serve under your supervision
                unless ordered otherwise by the management or automatically disqualified due to violation
                of company rules and regulations.
              </p>

              {/* Signature Lines */}
              <div className="grid grid-cols-2 gap-10 pt-2 border-t border-slate-100">
                <div className="text-center pt-4">
                  <div
                    style={{
                      borderBottom: "2px solid #cbd5f5",
                      marginBottom: "0.5rem",
                      height: "2rem"
                    }}
                  />
                  <p className="text-[9px] uppercase tracking-[0.15em] text-slate-400 font-semibold">
                    Acknowledged By
                  </p>
                </div>
                <div className="text-center pt-4">
                  <div
                    style={{
                      borderBottom: "2px solid #cbd5f5",
                      marginBottom: "0.5rem",
                      height: "2rem"
                    }}
                  />
                  <p className="text-[9px] uppercase tracking-[0.15em] text-slate-400 font-semibold">
                    Date Received
                  </p>
                </div>
              </div>

              {/* Authorized Signatory */}
              <div className="text-center mb-8">
                <div className="inline-block border-t-2 border-blue-900 pt-3 px-8">
                  <p className="text-sm font-bold text-blue-900">Engr. Ben Y. Lim, Jr.</p>
                  <p className="text-[11px] text-slate-500 mb-0">Vice - President</p>
                  <p className="text-[11px] text-slate-500 m-0">Industrial Relations Division</p>
                </div>
              </div>

            </div>
          </div>

          {/* Footer */}
          <div className="no-print px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end rounded-b-xl">
            <button
              onClick={onClose}
              className="px-5 py-1.5 bg-slate-600 text-white rounded-lg cursor-pointer hover:bg-slate-700 border-none transition-colors text-xs font-semibold tracking-wide uppercase"
            >
              Close
            </button>
          </div>

        </div>
      </div>
    </>

  );
};